'use client'

import { useState, useEffect } from 'react'

/**
 * Whether the browser viewport is at least `minWidth` px wide.
 *
 * The mirror image of `useIsMobile`, and deliberately a separate hook rather
 * than a second `useIsMobile(bigNumber)` call: a component that needs BOTH a
 * mobile test and a wider threshold would otherwise call the same hook twice
 * with different arguments, and `tests/helpers/ai-chat-panel-test-harness.ts`
 * mocks `useIsMobile` as `() => mocks.isMobile` — ignoring its breakpoint
 * argument — so the two calls would be indistinguishable to every test that
 * uses that harness. Separate hooks stay separately observable.
 *
 * Returns `false` on the server and on the first client render, so a caller
 * gating a layout commitment on it starts from "not wide enough" and moves to
 * the real answer after the first effect. That is the safe direction for the
 * one caller today (`AiChatPanel` reserving the sidecar track): the false
 * state is the pre-existing overlay, not a half-applied dock.
 */
export function useViewportMinWidth(minWidth: number): boolean {
  const [meetsMinWidth, setMeetsMinWidth] = useState(false)

  useEffect(() => {
    const check = () => {
      setMeetsMinWidth(window.innerWidth >= minWidth)
    }

    check()

    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [minWidth])

  return meetsMinWidth
}
